import React, { useState } from "react";
import WizardContext from "./WizardContext";
import WizardViewComponent from "./WizardViewComponent";

/**
 * WizardContextProvider - Manages all wizard state and navigation logic
 *
 * Props:
 *   - title: Title of the wizard
 *   - inputManifest: Initial/reference data object (optional, defaults to {})
 *   - emittedManifest: Initial emitted data object (optional, defaults to {})
 *   - onClose: Callback when wizard is closed (receives emittedManifest)
 *   - onFinish: Callback when wizard is finished (receives emittedManifest)
 *   - canClose: Whether the close button should be shown (default: true)
 *   - width: Wizard width (default: "1100px")
 *   - height: Wizard height (default: "600px")
 *   - modal: Whether to display as a centered modal (default: false)
 *   - children: WizardView.Page components
 */
const WizardContextProvider = (props) => {
    const pageCount = Array.isArray(props.children) ? props.children.length : 1;
    const [currentPage, setCurrentPage] = useState(0);
    const [inputManifest] = useState(
        props.inputManifest || props.manifest || {}
    );
    const [emittedManifest, setEmittedManifest] = useState(
        props.emittedManifest || {}
    );
    const [runtimeVariables, setRuntimeVariables] = useState({});
    const [title] = useState(props.title || "Wizard");
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [pageValidationStates, setPageValidationStates] = useState({});
    const [isFinishing, setIsFinishing] = useState(false);

    // Dimensions with defaults
    const wizardWidth = props.width || "1100px";
    const wizardHeight = props.height || "600px";
    const isModal = props.modal !== false; // Default to modal mode

    // Determine navigation states based on current page
    const canBack = currentPage > 0;
    const canNext = currentPage < pageCount - 1;
    const canFinish = currentPage === pageCount - 1;
    const canClose = props.canClose !== false;

    /**
     * Move to next page
     * Calls the onNextRequested callback on current page if it exists
     * Page can prevent navigation by setting event.detail.prevented = true
     * Page can update emittedManifest via event.detail.emittedManifest
     */
    const moveNext = () => {
        const pageComponent = Array.isArray(props.children)
            ? props.children[currentPage]
            : props.children;

        const onNextRequested = pageComponent?.props?.onNextRequested;

        if (onNextRequested) {
            const event = new CustomEvent("onNextRequested", {
                detail: {
                    inputManifest,
                    emittedManifest,
                    runtimeVariables,
                    prevented: false,
                },
                bubbles: false,
            });

            onNextRequested(event);

            if (event.detail.prevented) {
                return;
            }

            if (event.detail.emittedManifest) {
                setEmittedManifest(event.detail.emittedManifest);
            }

            if (event.detail.runtimeVariables) {
                setRuntimeVariables(event.detail.runtimeVariables);
            }
        }

        setCurrentPage(currentPage + 1);
    };

    /**
     * Move to previous page
     * Calls the onBackRequested callback on current page if it exists
     */
    const moveBack = () => {
        const pageComponent = Array.isArray(props.children)
            ? props.children[currentPage]
            : props.children;

        const onBackRequested = pageComponent?.props?.onBackRequested;

        if (onBackRequested) {
            const event = new CustomEvent("onBackRequested", {
                detail: {
                    inputManifest,
                    emittedManifest,
                    prevented: false,
                },
                bubbles: false,
            });

            onBackRequested(event);

            if (event.detail.prevented) {
                return;
            }

            if (event.detail.emittedManifest) {
                setEmittedManifest(event.detail.emittedManifest);
            }
        }

        setCurrentPage(currentPage - 1);
    };

    /**
     * Handle wizard finish - calls onFinish callback with final emittedManifest
     */
    const handleFinish = async () => {
        setIsFinishing(true);
        try {
            const pageComponent = Array.isArray(props.children)
                ? props.children[currentPage]
                : props.children;

            const onFinishRequested = pageComponent?.props?.onFinishRequested;

            if (onFinishRequested) {
                const event = new CustomEvent("onFinishRequested", {
                    detail: {
                        inputManifest,
                        emittedManifest,
                        prevented: false,
                    },
                    bubbles: false,
                });

                onFinishRequested(event);

                if (event.detail.prevented) {
                    return;
                }

                if (event.detail.emittedManifest) {
                    setEmittedManifest(event.detail.emittedManifest);
                    // Call the onFinish callback with the updated emittedManifest
                    if (props.onFinish) {
                        await props.onFinish(event.detail.emittedManifest);
                    }
                    return;
                }
            }

            // Call the onFinish callback with the final emittedManifest
            if (props.onFinish) {
                await props.onFinish(emittedManifest);
            }
        } finally {
            setIsFinishing(false);
        }
    };

    /**
     * Handle wizard close - calls onClose callback with current emittedManifest
     */
    const handleClose = () => {
        if (props.onClose) {
            props.onClose(emittedManifest);
        }
    };

    /**
     * Handle skip - move to next page
     */
    const handleSkip = () => {
        moveNext();
    };

    return (
        <WizardContext.Provider
            value={{
                currentPage,
                setCurrentPage,
                pageCount,
                canNext,
                canBack,
                canFinish,
                inputManifest,
                emittedManifest,
                setEmittedManifest,
                runtimeVariables,
                setRuntimeVariables,
                title,
                moveNext,
                moveBack,
                handleClose,
                handleFinish,
                handleSkip,
                canClose,
                pageValidationStates,
                setPageValidationStates,
                wizardWidth,
                wizardHeight,
                isModal,
                isFinishing,
            }}
        >
            <WizardViewComponent {...props} />
        </WizardContext.Provider>
    );
};

export default WizardContextProvider;
